import { isSupportedCountryCode } from '@seek/ca-myprofile-utils';
import {
  getAbsoluteAllowableMaximum,
  getAbsoluteAllowableMinimum,
} from '@seek/salary-data';
import { useEffect, useState } from 'react';

import { useAppConfig } from 'src/modules/appConfig';
import * as Schema from 'src/types/graphql';

import type { SalaryCurrency } from './SalaryItem';
import { formatSalary } from './utils';

interface SalaryRange {
  min: number;
  max: number;
  formattedMin?: string;
  formattedMax?: string;
}

const jobsdbAnnualRange = {
  AU: {
    min: 30000,
    max: 350000,
  },
  NZ: {
    min: 30000,
    max: 350000,
  },
  GB: {
    min: 5000,
    max: 200000,
  },
  HK: {
    min: 1,
    max: 1700000,
  },
  TH: {
    min: 1,
    max: 1400000,
  },
  MY: {
    min: 1,
    max: 700000,
  },
  SG: {
    min: 1,
    max: 310000,
  },
  PH: {
    min: 1,
    max: 1700000,
  },
  ID: {
    min: 1,
    max: 1400000000,
  },
};

const jobsdbMonthlyRange = {
  AU: {
    min: 3000,
    max: 35000,
  },
  NZ: {
    min: 3000,
    max: 35000,
  },
  GB: {
    min: 500,
    max: 20000,
  },
  HK: {
    min: 1,
    max: 120000,
  },
  TH: {
    min: 1,
    max: 999999,
  },
  MY: {
    min: 1,
    max: 50000,
  },
  SG: {
    min: 1,
    max: 22000,
  },
  PH: {
    min: 1,
    max: 120000,
  },
  ID: {
    min: 1,
    max: 100000000,
  },
};

const jobsdbHourlyRange = {
  AU: {
    min: 15,
    max: 175,
  },
  NZ: {
    min: 15,
    max: 175,
  },
  GB: {
    min: 5,
    max: 65,
  },
  HK: {
    min: 1,
    max: 700,
  },
  TH: {
    min: 1,
    max: 600,
  },
  MY: {
    min: 1,
    max: 300,
  },
  SG: {
    min: 1,
    max: 130,
  },
  PH: {
    min: 1,
    max: 700,
  },
  ID: {
    min: 1,
    max: 600000,
  },
};

const jobsdbSalaryRangeMapping = {
  [Schema.SalaryType2.Annual]: jobsdbAnnualRange,
  [Schema.SalaryType2.Monthly]: jobsdbMonthlyRange,
  [Schema.SalaryType2.Hourly]: jobsdbHourlyRange,
};

export const useSalaryRange = (
  countryCode: string,
  salaryType: Schema.SalaryType2,
  localeIntl: string,
  currencyCode: SalaryCurrency,
): SalaryRange | undefined => {
  const [salaryRange, setSalaryRange] = useState<SalaryRange | undefined>();
  const { isUnifiedSite } = useAppConfig();

  useEffect(() => {
    if (!isSupportedCountryCode(countryCode)) {
      setSalaryRange(undefined);
    } else {
      const jobsdbSalaryRanges =
        jobsdbSalaryRangeMapping[salaryType][countryCode];

      const salaryFrequency = salaryType.toUpperCase() as Uppercase<
        typeof salaryType
      >;

      const min = isUnifiedSite
        ? getAbsoluteAllowableMinimum({
            currency: currencyCode,
            frequency: salaryFrequency,
          })
        : jobsdbSalaryRanges.min;
      const max = isUnifiedSite
        ? getAbsoluteAllowableMaximum({
            currency: currencyCode,
            frequency: salaryFrequency,
          })
        : jobsdbSalaryRanges.max;

      setSalaryRange({
        min,
        max,
        formattedMin: formatSalary({ amount: min, currencyCode, localeIntl }),
        formattedMax: formatSalary({ amount: max, currencyCode, localeIntl }),
      });
    }
  }, [countryCode, currencyCode, isUnifiedSite, localeIntl, salaryType]);

  return salaryRange;
};
